import React, { Suspense, memo } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO/SEO';
import { Link } from 'react-router-dom';
import { FaGithub, FaDev, FaCode, FaServer, FaDatabase, FaCloud } from 'react-icons/fa';
import ChatBot from './../components/ChatBot/ChatBot';


const ThreeBackground = React.lazy(() => import('../components/ThreeBackground'));

const services = [
  {
    title: "Full Stack Development",
    icon: <FaCode />,
    description: "End-to-end web application development using MERN stack",
  },
  {
    title: "API Development",
    icon: <FaServer />,
    description: "RESTful API design and implementation",
  },
  {
    title: "Cloud Solutions",
    icon: <FaCloud />,
    description: "AWS cloud architecture and deployment solutions",
  },
  {
    title: "Database Design",
    icon: <FaDatabase />,
    description: "MongoDB database architecture and optimization",
  },
];

const About = () => {
  return (
    <div className="min-h-screen bg-[#0A1121] text-white">
      <SEO
        title="Mahmudur Rahman's - About"
        description="Full Stack Developer specializing in MERN stack development. Building modern web applications with React, Node.js, Socket, and MongoDB."
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740488531/about-banner_r4kaqj.jpg"
        url="https://devplus.fun/about"
        keywords="Full Stack Developer, MERN Stack, React, Node.js, MongoDB, Web Development, JavaScript, Next.js, Express.js, about, about mahmud, mahmud's about, about mahmudur rahman, about section"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Three.js Background */}
             <Suspense fallback={<div className="absolute inset-0 z-0 bg-gray-900" />}>
               <div className="absolute inset-0 z-0">
                 <ThreeBackground />
               </div>
             </Suspense>

        <motion.div
          className="container mx-auto px-6 z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Building Digital Solutions
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-8">
            Full Stack Developer specializing in MERN stack.
            Passionate about creating scalable and efficient web solutions.
          </p>
          <div className="flex gap-6">
            <a
              href="https://github.com/mahmud-r-farhan"
              target='_blank'
              className="flex items-center gap-2 px-6 py-3 bg-white/10 rounded-full hover:bg-white/20 transition-all" rel="noreferrer"
            >
              <FaGithub /> GitHub
            </a>
            <a
              href="https://dev.to/mahmud-r-farhan"
              target='_blank'
              className="flex items-center gap-2 px-6 py-3 bg-white/10 rounded-full hover:bg-white/20 transition-all" rel="noreferrer"
            >
              <FaDev /> Dev.to
            </a>
          </div>
        </motion.div>
      </section>

      {/* Profile Section */}
      <section className="py-20 bg-gradient-to-b from-[#0A1121] to-[#111827]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740500702/farhanstack.dev_gmail.com_ibsabz.png"
                alt="Professional Profile"
                className="rounded-full w-80 h-80 mx-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1121] opacity-30 rounded-2xl" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-6">About My Approach</h2>
              <p className="text-gray-300 leading-relaxed">
                With over 2 years of experience in full-stack development, I specialize in
                building scalable web applications using modern technologies. My approach
                combines technical expertise with a strong focus on clean code and user experience.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">37+</h4>
                  <p className="text-gray-400">Projects Completed</p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">21+</h4>
                  <p className="text-gray-400">Happy Clients</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#111827]">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center mb-16"
          >
            Services & Expertise
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 p-6 rounded-xl hover:bg-white/10 transition-all"
              >
                <div className="text-4xl text-blue-400 mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-400">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-20 bg-[#0A1121]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Listen & Watch</h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Audio Player */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold mb-6">Listen to My Introduction</h3>
              <audio controls 
               onContextMenu={(e) => e.preventDefault()}
                controlsList="nodownload"
              className="w-full">
                <source
                  src="https://res.cloudinary.com/dqovjmmlx/video/upload/f_auto:video,q_auto/v1/portfolio/rggy1ck80iuik69zmd8c"
                  type="audio/mpeg"
                />
                Your browser does not support the audio element.
              </audio>
            </motion.div>

            {/* Video Player */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold mb-6">Watch My Introduction</h3>
              <video controls 
               controlsList="nodownload" 
               onContextMenu={(e) => e.preventDefault()}
               className="w-full rounded-lg">
                <source
                  src="https://res.cloudinary.com/dqovjmmlx/video/upload/f_auto:video,q_auto/v1/portfolio/e2kbswvvixnpfcpgfzmy"
                  type="video/mp4"
                />
                Your browser does not support the video element.
              </video>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-t from-[#0A1121] to-[#111827]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">Let's Build Something Amazing</h2>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-blue-500 rounded-full font-medium hover:bg-blue-600 transition-colors"
            >
              Get In Touch
            </motion.button>
          </Link>
        </div>
      </section>
      <ChatBot />
    </div>
  );
};

export default memo(About);